import React, { type FunctionComponent, useState } from 'react'
import { CardBody, RadioWithLabel } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { Input } from '../../../shared-styled-components/SharedStyledComponents'
import { TimePickerField } from './TimePickerField'
import { CronExpressionDisplay } from './CronExpressionDisplay'
import { convertDailyToCron } from './cronUtils'

const StyledCardBody = styled(CardBody)({
  padding: '12px',
})

interface Props {
  onScheduleChange?: (schedule: {
    type: 'every_n_days' | 'weekdays'
    days?: number
    startTime: string
  }) => void
  defaultSchedule: {
    type: 'every_n_days' | 'weekdays'
    days: number
    startTime: string
  }
}

const ScheduleSection = styled.div({
  marginBottom: '8px',
})

const StyledInput = styled(Input)({
  width: '80px',
  margin: '0 8px',
})

export const DailySchedule: FunctionComponent<React.PropsWithChildren<Props>> = ({
  onScheduleChange,
  defaultSchedule,
}) => {
  const [scheduleType, setScheduleType] = useState<'every_n_days' | 'weekdays'>(
    defaultSchedule.type
  )
  const [days, setDays] = useState<number>(defaultSchedule.days)
  const [startTime, setStartTime] = useState<string>(defaultSchedule.startTime)
  const { cron, readable } = convertDailyToCron({ type: scheduleType, days, startTime })

  const handleScheduleChange = (type: 'every_n_days' | 'weekdays') => {
    setScheduleType(type)
    onScheduleChange?.({
      type,
      days: type === 'every_n_days' ? days : undefined,
      startTime,
    })
  }

  return (
    <StyledCardBody>
      <ScheduleSection>
        <div>
          <RadioWithLabel
            id="schedule-every-n-days"
            value="every_n_days"
            checked={scheduleType === 'every_n_days'}
            onChange={() => handleScheduleChange('every_n_days')}
            label={
              <>
                Every
                <StyledInput
                  type="number"
                  min={1}
                  value={days}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newDays = parseInt(e.target.value)
                    setDays(newDays)
                    onScheduleChange?.({
                      type: scheduleType,
                      days: scheduleType === 'every_n_days' ? newDays : undefined,
                      startTime,
                    })
                  }}
                  disabled={scheduleType !== 'every_n_days'}
                />
                days
              </>
            }
          />
          <RadioWithLabel
            id="schedule-weekdays"
            value="weekdays"
            checked={scheduleType === 'weekdays'}
            onChange={() => handleScheduleChange('weekdays')}
            label="Every weekday (Monday to Friday)"
          />
        </div>
      </ScheduleSection>

      <TimePickerField
        value={startTime}
        onChange={newTime => {
          setStartTime(newTime)
          onScheduleChange?.({
            type: scheduleType,
            days: scheduleType === 'every_n_days' ? days : undefined,
            startTime: newTime,
          })
        }}
      />

      <CronExpressionDisplay cronExpression={cron} cronDescription={readable} />
    </StyledCardBody>
  )
}
